// src/components/OS/OSTable.jsx
import React from 'react';
import {
  FaEdit, FaEye, FaPlay, FaCheck,
  FaFileInvoiceDollar, FaTrashAlt, FaSyncAlt
} from 'react-icons/fa';

/* ---------- utils de status ---------- */
const normalizeStatus = (s = '') => String(s || '').toLowerCase().trim();
const isCancelado = (s = '') => normalizeStatus(s).includes('cancelad');

const fallbackNfeView = (status = '') => {
  const s = normalizeStatus(status);
  if (!s) return { text: 'Não emitida', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-400' };
  if (s.includes('autoriz')) return { text: 'Autorizada', badge: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' };
  if (s.includes('cancelad')) return { text: 'Cancelada', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-500' };
  if (s.includes('deneg')) return { text: 'Denegada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('erro') || s.includes('rejei') || s.includes('falha'))
    return { text: 'Rejeitada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('process') || s.includes('aguard') || s.includes('pend'))
    return { text: 'Aguardando autorização', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
  return { text: status, badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
};

const fallbackNfseView = (status = '') => {
  const s = normalizeStatus(status);
  if (!s) return { text: 'Não emitida', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-400' };
  if (s.includes('cancelad')) return { text: 'Cancelada', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-500' };
  if (s.includes('autoriz') || s.includes('emitid') || s.includes('aprov'))
    return { text: 'Emitida', badge: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' };
  if (s.includes('erro') || s.includes('rejei') || s.includes('falha'))
    return { text: 'Rejeitada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('process') || s.includes('aguard') || s.includes('pend'))
    return { text: 'Aguardando autorização', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
  return { text: status, badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
};

// prioriza erro
const asErrorView = (view, hasError) =>
  hasError ? { text: 'Rejeitada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' } : view;

// helpers “emitida”
const isNfeEmitida = (status) => {
  const s = normalizeStatus(status);
  return (s.includes('autoriz') || s.includes('emitid') || s.includes('aprov')) && !isCancelado(s);
};
const isNfseEmitida = (status) => {
  const s = normalizeStatus(status);
  return (s.includes('autoriz') || s.includes('emitid') || s.includes('aprov')) && !isCancelado(s);
};

const Chip = ({ text, className = '', dotClass = '' }) => (
  <span className={`inline-flex items-center gap-2 text-xs font-semibold px-2 py-1 rounded border ${className}`}>
    <i className={`w-2 h-2 rounded-full ${dotClass}`} />
    {text}
  </span>
);

// Links da Focus
const ensureFocusAbsolute = (maybePath) =>
  maybePath && !/^https?:\/\//i.test(maybePath)
    ? `https://api.focusnfe.com.br${maybePath}`
    : maybePath || '';

const looksLikeFocus = (u = '') => /focusnfe\.com\.br/i.test(u) || /^\//.test(u);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

/* ===== Tooltip simples (tailwind, sem lib) ===== */
const Tooltip = ({ label, children }) => (
  <span className="relative inline-flex group">
    {children}
    <span className="pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-gray-900 text-white text-[10px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-20">
      {label}
    </span>
  </span>
);

/* ===== Botões tom-sobre-tom (ícone-only) ===== */
const toneStyles = (tone) => {
  switch (tone) {
    case 'primary':
      return 'text-blue-800 bg-blue-100 hover:bg-blue-200 dark:text-blue-300 dark:bg-blue-900/50 dark:hover:bg-blue-900';
    case 'success':
      return 'text-green-800 bg-green-100 hover:bg-green-200 dark:text-green-300 dark:bg-green-900/50 dark:hover:bg-green-900';
    case 'warning':
      return 'text-yellow-800 bg-yellow-100 hover:bg-yellow-200 dark:text-yellow-300 dark:bg-yellow-900/50';
    case 'danger':
      return 'text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-300 dark:bg-red-900/50 dark:hover:bg-red-900';
    default:
      return 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700';
  }
};
const ActionIcon = ({ as='button', tone='neutral', tooltip, title, disabled, className='', children, ...props }) => {
  const base = `inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-semibold transition ${toneStyles(tone)} ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${className}`;
  const El = as === 'a'
    ? <a className={base} title={title} {...props}>{children}</a>
    : <button className={base} title={title} disabled={disabled} {...props}>{children}</button>;
  return tooltip ? <Tooltip label={tooltip}>{El}</Tooltip> : El;
};

const OSTable = ({
  osFiltradas,
  statusColors,
  nfseLoading,
  nfeLoading,
  onEditar,
  onIniciarAtendimento,
  onFinalizarOS,
  onExcluir,

  onEmitirNfse,
  onEmitirNfe,
  onEmitirNota, // fallback

  onAbrirDetalhe,
  onAbrirNotasFiscais,

  onConsultarNfse,
  onConsultarNfe,

  nfStatusView = fallbackNfeView,
  nfseStatusView = fallbackNfseView,

  formatarData = (d) => {
    try {
      const date = d?.toDate ? d.toDate() : d instanceof Date ? d : null;
      return date ? date.toLocaleString('pt-BR') : '-';
    } catch {
      return '-';
    }
  }
}) => (
  <div className="w-full max-w-[1800px] mx-auto rounded-2xl border dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
    <div className="overflow-x-auto rounded-2xl">
      <table className="w-full text-sm">
        <caption className="sr-only">Lista de Ordens de Serviço</caption>

        {/* Cabeçalho padronizado */}
        <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800">
          <tr>
            {[
              ['Nº OS','left'],
              ['Cliente','left'],
              ['Veículo','left','hidden md:table-cell'],
              ['Status','left'],
              ['Data','left','hidden sm:table-cell'],
              ['Total','left'],
              ['Status NFS-e','left'],
              ['Status NF-e','left'],
              ['Ações','center'],
            ].map(([label, align, extra]) => (
              <th
                key={label}
                className={`px-3 py-3 text-xs uppercase tracking-wide text-gray-900 dark:text-gray-300 ${align === 'left' ? 'text-left' : 'text-center'} ${extra || ''}`}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {osFiltradas.length === 0 ? (
            <tr>
              <td colSpan={9} className="text-center text-gray-400 py-6">
                Nenhuma OS encontrada.
              </td>
            </tr>
          ) : (
            osFiltradas.map(os => {
              const temServico = (os.servicos?.length || 0) > 0;
              const temProduto = (os.produtos?.length || 0) > 0;

              const nfeAutorizada  = isNfeEmitida(os.nfeStatus);
              const nfseAutorizada = !!os.nfseEmitida || isNfseEmitida(os.nfseStatus);
              const temNotaFiscal  = (temProduto && nfeAutorizada) || (temServico && nfseAutorizada);

              // views (com prioridade para erro/rejeição)
              const nfseBase = nfseStatusView(os.nfseStatus);
              const nfeBase  = nfStatusView(os.nfeStatus);
              const nfseErro = /erro|rejei|deneg/.test(normalizeStatus(os.nfseStatus || '')) || !!os.nfseUltimoErro;
              const nfeErro  = /erro|rejei|deneg/.test(normalizeStatus(os.nfeStatus  || '')) || !!os.nfeUltimoErro;
              const nfseV    = asErrorView(nfseBase, nfseErro);
              const nfeV     = asErrorView(nfeBase,  nfeErro);

              const emitirNfse = () => (onEmitirNfse ? onEmitirNfse(os) : onEmitirNota?.(os, 'nfse'));
              const emitirNfe  = () => (onEmitirNfe  ? onEmitirNfe(os)  : onEmitirNota?.(os, 'nfe'));

              const nfseProcessando = /process|aguard|pend/i.test(String(os.nfseStatus || ''));
              const nfeProcessando  = /process|aguard|pend/i.test(String(os.nfeStatus  || ''));

              // --- NF-e: prioriza backend DANFe se tiver chave; senão usa link salvo apenas se NÃO for Focus
              const nfeChave   = (os.nfeChave || '').replace(/^NFe/, '');
              const savedDanfe = ensureFocusAbsolute(os.nfeDanfeUrl || '');
              const danfeHref  =
                (nfeChave ? `${BACKEND_URL}/api/focus/baixar-danfe/${nfeChave}` : '') ||
                (!looksLikeFocus(savedDanfe) ? savedDanfe : '');

              // --- NFS-e: usar viewer do backend quando link salvo for da Focus (ou quando só temos ref)
              const nfseRef       = os.nfseRef || '';
              const nfseViewer    = nfseRef ? `${BACKEND_URL}/api/focus/visualizar-nfse/${encodeURIComponent(nfseRef)}` : '';
              const savedNfseLink = ensureFocusAbsolute(os.nfseUrl || '');
              const nfseUrl       =
                (looksLikeFocus(savedNfseLink) ? nfseViewer : savedNfseLink) ||
                nfseViewer ||
                '';

              return (
                <tr
                  key={os.id}
                  className="border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition even:bg-gray-50 dark:even:bg-gray-800"
                >
                  <td className="px-3 py-3 whitespace-nowrap font-semibold text-gray-900 dark:text-gray-100">#{os.numeroOS ?? '...'}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{os.clienteNome || '-'}</td>
                  <td className="px-3 py-3 whitespace-nowrap hidden md:table-cell">{os.veiculoDescricao || '-'}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold border ${statusColors?.[os.status] || ''}`}
                      aria-label={`Status: ${os.status}`}
                      title={`Status: ${os.status}`}
                    >
                      {os.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap hidden sm:table-cell">{formatarData(os.dataAbertura)}</td>
                  <td className="px-3 py-3 whitespace-nowrap font-semibold text-green-700 dark:text-green-400">
                    R$ {(Number(os.valorTotalGeral) || 0).toFixed(2)}
                  </td>

                  {/* Status NFS-e */}
                  <td className="px-3 py-3 align-top">
                    {temServico && os.status === 'Finalizada'
                      ? <Chip text={nfseV.text} className={nfseV.badge} dotClass={nfseV.dot} />
                      : <span className="text-gray-400">-</span>}
                  </td>

                  {/* Status NF-e */}
                  <td className="px-3 py-3 align-top">
                    {temProduto && os.status === 'Finalizada'
                      ? <Chip text={nfeV.text} className={nfeV.badge} dotClass={nfeV.dot} />
                      : <span className="text-gray-400">-</span>}
                  </td>

                  {/* AÇÕES — tom-sobre-tom */}
                  <td className="px-3 py-3 text-center whitespace-nowrap">
                    <div className="flex flex-row flex-nowrap gap-2 justify-center items-center">
                      {os.status === 'Aberta' && (
                        <>
                          <ActionIcon
                            tone="neutral"
                            title="Editar OS"
                            tooltip="Editar OS"
                            onClick={() => onEditar?.(os)}
                          >
                            <FaEdit />
                          </ActionIcon>
                          <ActionIcon
                            tone="warning"
                            title="Iniciar atendimento"
                            tooltip="Iniciar atendimento"
                            onClick={() => onIniciarAtendimento?.(os)}
                          >
                            <FaPlay />
                          </ActionIcon>
                        </>
                      )}

                      {os.status === 'Em andamento' && (
                        <ActionIcon
                          tone="success"
                          title="Finalizar OS"
                          tooltip="Finalizar OS"
                          onClick={() => onFinalizarOS?.(os)}
                        >
                          <FaCheck />
                        </ActionIcon>
                      )}

                      <ActionIcon
                        tone="primary"
                        title="Detalhar OS"
                        tooltip="Detalhar OS"
                        onClick={() => onAbrirDetalhe?.(os)}
                      >
                        <FaEye />
                      </ActionIcon>

                      {/* ======== NFS-e ======== */}
                      {(onEmitirNfse || onEmitirNota) &&
                        os.status === 'Finalizada' && temServico && !nfseAutorizada && (
                          <ActionIcon
                            tone="primary"
                            title={nfseV.text === 'Rejeitada' ? 'Emitir novamente NFS-e' : 'Emitir NFS-e'}
                            tooltip={nfseV.text === 'Rejeitada' ? 'Emitir novamente NFS-e' : 'Emitir NFS-e'}
                            onClick={emitirNfse}
                            disabled={!!nfseLoading?.[os.id] || (nfseProcessando && !nfseErro)}
                          >
                            <FaFileInvoiceDollar />
                          </ActionIcon>
                        )
                      }

                      {(onEmitirNfse || onEmitirNota) &&
                        os.status === 'Finalizada' && temServico && nfseAutorizada && (
                          <ActionIcon
                            tone="primary"
                            title="NFS-e já emitida"
                            tooltip="NFS-e já emitida"
                            disabled
                          >
                            <FaFileInvoiceDollar />
                          </ActionIcon>
                        )
                      }

                      {typeof onConsultarNfse === 'function' &&
                        os.status === 'Finalizada' && temServico && os.nfseRef && !nfseAutorizada && (
                          <ActionIcon
                            tone="primary"
                            title="Atualizar status da NFS-e"
                            tooltip="Atualizar status da NFS-e"
                            onClick={() => onConsultarNfse(os)}
                            disabled={!!nfseLoading?.[os.id]}
                          >
                            <FaSyncAlt />
                          </ActionIcon>
                        )
                      }

                      {os.status === 'Finalizada' && temServico && nfseAutorizada && nfseUrl && (
                        <ActionIcon
                          as="a"
                          tone="primary"
                          title="Ver NFS-e"
                          tooltip="Ver NFS-e"
                          href={nfseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaFileInvoiceDollar />
                        </ActionIcon>
                      )}

                      {/* ======== NF-e ======== */}
                      {(onEmitirNfe || onEmitirNota) &&
                        os.status === 'Finalizada' && temProduto && !nfeAutorizada && (
                          <ActionIcon
                            tone="success"
                            title={nfeV.text === 'Rejeitada' ? 'Emitir novamente NF-e' : 'Emitir NF-e'}
                            tooltip={nfeV.text === 'Rejeitada' ? 'Emitir novamente NF-e' : 'Emitir NF-e'}
                            onClick={emitirNfe}
                            disabled={!!nfeLoading?.[os.id] || (nfeProcessando && !nfeErro)}
                          >
                            <FaFileInvoiceDollar />
                          </ActionIcon>
                        )
                      }

                      {(onEmitirNfe || onEmitirNota) &&
                        os.status === 'Finalizada' && temProduto && nfeAutorizada && (
                          <ActionIcon
                            tone="success"
                            title="NF-e já emitida"
                            tooltip="NF-e já emitida"
                            disabled
                          >
                            <FaFileInvoiceDollar />
                          </ActionIcon>
                        )
                      }

                      {typeof onConsultarNfe === 'function' &&
                        os.status === 'Finalizada' && temProduto && (os.nfeRef || os.nfeChave) && !nfeAutorizada && (
                          <ActionIcon
                            tone="success"
                            title="Atualizar status da NF-e"
                            tooltip="Atualizar status da NF-e"
                            onClick={() => onConsultarNfe(os)}
                            disabled={!!nfeLoading?.[os.id]}
                          >
                            <FaSyncAlt />
                          </ActionIcon>
                        )
                      }

                      {os.status === 'Finalizada' && temProduto && nfeAutorizada && danfeHref && (
                        <ActionIcon
                          as="a"
                          tone="success"
                          title="Ver NF-e (DANFe)"
                          tooltip="Ver NF-e (DANFe)"
                          href={danfeHref}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaFileInvoiceDollar />
                        </ActionIcon>
                      )}

                      {typeof onAbrirNotasFiscais === 'function' &&
                        os.status === 'Finalizada' && temNotaFiscal && (
                          <ActionIcon
                            tone="neutral"
                            title="Ver detalhes e cancelar"
                            tooltip="Ver detalhes e cancelar"
                            onClick={() => onAbrirNotasFiscais(os)}
                          >
                            <FaFileInvoiceDollar />
                          </ActionIcon>
                        )
                      }

                      {os.status !== 'Finalizada' && (
                        <ActionIcon
                          tone="danger"
                          title="Excluir OS"
                          tooltip="Excluir OS"
                          onClick={() => onExcluir?.(os)}
                        >
                          <FaTrashAlt />
                        </ActionIcon>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default OSTable;
